// src/views/AppViews/DesignSystemExamples.jsx
import React, { useState } from 'react';
import { Button, Input, Checkbox, Radio, Toggle, Typography, Select, SelectDate, AutoCompleteSelect, Table, Dropdown, Card, Chart, SweetAlert, FileInput, Tooltip, Steps, CurrencyInput } from '../../design-system';
import { colors } from '../../design-system/theme/colors';
import Address from '../../_components/Address';

const sampleOptions = [
  { value: 'option1', label: 'Option 1' },
  { value: 'option2', label: 'Option 2' },
  { value: 'option3', label: 'Option 3' },
];

const ExampleSection = ({ title, children }) => (
  <section style={{ marginBottom: '3rem' }}>
    <Typography size="lg" style={{ marginBottom: '1rem', display: 'block', fontWeight: 'bold' }}>{title}</Typography>
    {children}
  </section>
);

const ColorBox = ({ color, name }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1rem' }}>
    <div style={{ width: '80px', height: '50px', backgroundColor: color, border: '1px solid #ddd', borderRadius: '4px' }}></div>
    <Typography size="xs" style={{ marginTop: '0.5rem' }}>{name}</Typography>
  </div>
);

const DesignSystemExamples = () => {
  const [activeTab, setActiveTab] = useState('cores_tipografia');
  const [activeAlert, setActiveAlert] = useState(null);
  const [addressData, setAddressData] = useState({});

  const closeAlert = () => setActiveAlert(null);

  const menuItems = [
    { id: 'cores_tipografia', label: 'Cores e Tipografia', icon: 'fa-palette' },
    { id: 'botoes', label: 'Botões', icon: 'fa-mouse-pointer' },
    { id: 'inputs', label: 'Inputs & Seletores', icon: 'fa-keyboard' },
    { id: 'tabelas', label: 'Tabelas', icon: 'fa-table' },
    { id: 'cards', label: 'Cartões', icon: 'fa-id-card' },
    { id: 'charts', label: 'Gráficos', icon: 'fa-chart-pie' },
    { id: 'alertas', label: 'Alertas', icon: 'fa-bell' },
    { id: 'arquivos_tooltips', label: 'Arquivos & Tooltips', icon: 'fa-file-upload' },
    { id: 'passos', label: 'Passos (Steps)', icon: 'fa-list-ol' },
    { id: 'address', label: 'Componente Endereço', icon: 'fa-map-marked-alt' },
  ];

  const renderCoresTipografia = () => (
    <React.Fragment>
      <ExampleSection title="Brand Colors Palette">
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <ColorBox color={colors.brandYellow} name="brandYellow" />
          <ColorBox color={colors.brandDarkBlue} name="brandDarkBlue" />
          <ColorBox color={colors.brandLightBlue} name="brandLightBlue" />
          <ColorBox color={colors.brandIce} name="brandIce" />
          <ColorBox color={colors.brandOffWhite} name="brandOffWhite" />
          <ColorBox color={colors.brandLightGray} name="brandLightGray" />
          <ColorBox color={colors.brandMediumGray} name="brandMediumGray" />
        </div>
      </ExampleSection>
      
      <ExampleSection title="Typography Variants (Figma UI Kit)">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          Escala tipográfica baseada no Figma Dashboard UI Kit (Família Inter).
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
          <div>
            <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold', color: colors.brandLightGray }}>HEADINGS</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
              <Typography variant="h1">Heading 1</Typography>
              <Typography variant="h2">Heading 2</Typography>
              <Typography variant="h3">Heading 3</Typography>
              <Typography variant="h4">Heading 4</Typography>
              <Typography variant="h5">Heading 5</Typography>
              <Typography variant="h6">Heading 6</Typography>
            </div>
          </div>
          <div>
            <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold', color: colors.brandLightGray }}>DISPLAY</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
              <Typography variant="display1">Display 1</Typography>
              <Typography variant="display2">Display 2</Typography>
              <Typography variant="display3">Display 3</Typography>
              <Typography variant="display4">Display 4</Typography>
              <Typography variant="display5">Display 5</Typography>
              <Typography variant="display6">Display 6</Typography>
            </div>
          </div>
          <div>
            <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold', color: colors.brandLightGray }}>BODY / TEXT</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
              <Typography variant="lead">Lead Text / Blockquote</Typography>
              <Typography variant="large">Paragraph Large</Typography>
              <Typography variant="medium">Paragraph Medium</Typography>
              <Typography variant="small">Paragraph Small / List</Typography>
              <Typography variant="xsText">Extra Small Text</Typography>
            </div>
          </div>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderBotoes = () => (
    <React.Fragment>
      <ExampleSection title="Hierarquia de Ações (Botões)">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          Botões possibilitam o usuário executar uma ação.
        </Typography>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
          <div>
            <Typography size="lg" style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 'bold' }}>1. Botão Primário</Typography>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <Button variant="primary">Confirmar</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>REPOUSO</Typography>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button variant="primary" disabled>Confirmar</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>DESABILITADO</Typography>
              </div>
            </div>
          </div>
          <div>
            <Typography size="lg" style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 'bold' }}>2. Botão Secundário</Typography>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <Button variant="secondary">Ver mais</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>REPOUSO</Typography>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button variant="secondary" disabled>Ver mais</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>DESABILITADO</Typography>
              </div>
            </div>
          </div>
          <div>
            <Typography size="lg" style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 'bold' }}>3. Botão Terciário</Typography>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <Button variant="tertiary">Cancelar</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>REPOUSO</Typography>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button variant="tertiary" disabled>Cancelar</Button>
                <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>DESABILITADO</Typography>
              </div>
            </div>
          </div>
        </div>
      </ExampleSection>

      <ExampleSection title="Variações de Cores (Botões)">
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <Button variant="success">Sucesso</Button>
            <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>SUCCESS</Typography>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Button variant="warning">Atenção</Button>
            <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>WARNING</Typography>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Button variant="danger">Erro / Perigo</Button>
            <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>DANGER</Typography>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Button variant="info">Informação</Button>
            <Typography size="xs" style={{ display: 'block', marginTop: '0.5rem', color: colors.brandLightGray }}>INFO</Typography>
          </div>
        </div>
      </ExampleSection>

      <ExampleSection title="Figma UI Kit Botões (Primary, Light e Dark)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Primary</Typography>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" shape="rounded" size="lg">Button</Button>
              <Button variant="primary" shape="rectangle" size="lg">Button</Button>
              <Button variant="primary" shape="rounded" size="md">Button</Button>
              <Button variant="primary" shape="rectangle" size="md">Button</Button>
              <Button variant="primary" shape="rounded" size="sm">Button</Button>
              <Button variant="primary" shape="rectangle" size="sm">Button</Button>
            </div>
          </div>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Light</Typography>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="light" shape="rounded" size="lg">Button</Button>
              <Button variant="light" shape="rectangle" size="lg">Button</Button>
              <Button variant="light" shape="rounded" size="md">Button</Button>
              <Button variant="light" shape="rectangle" size="md">Button</Button>
              <Button variant="light" shape="rounded" size="sm">Button</Button>
              <Button variant="light" shape="rectangle" size="sm">Button</Button>
            </div>
          </div>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Dark</Typography>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="dark" shape="rounded" size="lg">Button</Button>
              <Button variant="dark" shape="rectangle" size="lg">Button</Button>
              <Button variant="dark" shape="rounded" size="md">Button</Button>
              <Button variant="dark" shape="rectangle" size="md">Button</Button>
              <Button variant="dark" shape="rounded" size="sm">Button</Button>
              <Button variant="dark" shape="rectangle" size="sm">Button</Button>
            </div>
          </div>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderInputs = () => (
    <React.Fragment>
      <ExampleSection title="Campos de texto (Inputs)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem', maxWidth: '400px' }}>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>REPOUSO</Typography>
            <Input label="Rótulo" placeholder="Placeholder" />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>FOCADO / HOVER</Typography>
            <Input label="Rótulo" placeholder="Placeholder" style={{ borderColor: colors.brandDarkBlue, boxShadow: '0 0 0 3px rgba(0, 169, 255, 0.25)' }} />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>PREENCHIDO</Typography>
            <Input label="Rótulo" defaultValue="Texto inserido" />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>COM ERRO</Typography>
            <Input label="Rótulo" defaultValue="Texto i" error="Apenas letras ou números" />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>DESABILITADO</Typography>
            <Input label="Rótulo" placeholder="Placeholder" disabled />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>ÍCONE À DIREITA E ESQUERDA</Typography>
            <Input label="Buscar" placeholder="Pesquisar..." icon={<i className="fa fa-search" />} style={{ marginBottom: 10 }} />
            <Input label="Localização" placeholder="Digite o endereço..." iconPosition="left" icon={<i className="fa fa-map-marker-alt" />} />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>SENHA</Typography>
            <Input label="Senha" type="password" placeholder="Digite sua senha" />
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>MÁSCARAS (CPF, CNPJ, Telefone, CEP)</Typography>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              <Input label="CPF" type="cpf" placeholder="000.000.000-00" />
              <Input label="CNPJ" type="cnpj" placeholder="00.000.000/0000-00" />
              <Input label="Telefone" type="telefone" placeholder="(00) 00000-0000" />
              <Input label="CEP" type="cep" placeholder="00000-000" />
            </div>
          </div>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>ENTRADA DE MOEDA (CurrencyInput)</Typography>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              <CurrencyInput label="Preço Unitário" value={10.50} onChange={(e, val) => () => {}} />
              <CurrencyInput label="Faturamento Estimado" currencySymbol="US$" value={250000.75} placeholder="0.00" onChange={(e, val) => () => {}} />
              <CurrencyInput label="Valor Bloqueado (Desabilitado)" value={450.00} disabled />
            </div>
          </div>
        </div>
      </ExampleSection>

      <ExampleSection title="Figma UI Kit Inputs (Checkbox, Radio, Toggle)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Checkbox</Typography>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Checkbox label="Default" />
              <Checkbox label="Checked" checked readOnly />
              <Checkbox label="Disabled" disabled />
            </div>
          </div>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Radio</Typography>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Radio name="demo" label="Default" />
              <Radio name="demo" label="Checked" checked readOnly />
              <Radio name="demo2" label="Disabled" disabled />
            </div>
          </div>
          <div>
            <Typography size="sm" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Toggle / Switch</Typography>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Toggle label="Default" />
              <Toggle label="Active" checked readOnly />
              <Toggle label="Disabled" disabled />
            </div>
          </div>
        </div>
      </ExampleSection>

      <ExampleSection title="Selects e Date Pickers">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4rem' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Select Padrão & AutoComplete</Typography>
            <Select size="sm" options={sampleOptions} label="Select Simples" style={{ marginBottom: '1rem', display: 'block' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <AutoCompleteSelect label="Repouso / Padrão" options={sampleOptions} placeholder="Digite para buscar..." />
              <AutoCompleteSelect label="Preenchido" options={sampleOptions} value="option2" />
            </div>
          </div>

          <div style={{ flex: '1', minWidth: '300px' }}>
            <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Date picker WEB (SelectDate)</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <SelectDate name="date_rest" label="Repouso" />
              <SelectDate name="date_range" label="Preenchido com intervalo" value="30/07/2019 - 06/08/2019" isRange />
            </div>
          </div>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderTabelas = () => (
    <React.Fragment>
      <ExampleSection title="Tabelas Premium (Responsive Table)">
        <div style={{ marginBottom: '3rem' }}>
          <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Tabela com Busca Interna e Filtros</Typography>
          <Table 
            hover 
            searchable 
            paginate 
            onFilterClick={() => () => {}}
            onSettingsClick={() => () => {}}
            searchPlaceholder="Buscar clientes, documentos..."
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Cliente</Table.HeaderCell>
                <Table.HeaderCell>Documento</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>João Silva</Table.Cell>
                <Table.Cell>123.456.789-00</Table.Cell>
                <Table.Cell><span style={{ color: '#28a745', fontWeight: 600 }}>Ativo</span></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Maria Oliveira</Table.Cell>
                <Table.Cell>987.654.321-11</Table.Cell>
                <Table.Cell><span style={{ color: '#dc3545', fontWeight: 600 }}>Inativo</span></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Carlos Mendes</Table.Cell>
                <Table.Cell>456.123.789-22</Table.Cell>
                <Table.Cell><span style={{ color: '#28a745', fontWeight: 600 }}>Ativo</span></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Ana Souza</Table.Cell>
                <Table.Cell>321.654.987-33</Table.Cell>
                <Table.Cell><span style={{ color: '#ffc107', fontWeight: 600 }}>Pendente</span></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        <div style={{ marginBottom: '3rem' }}>
          <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Tabela Padrão com Hover</Typography>
          <Table hover>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Cliente</Table.HeaderCell>
                <Table.HeaderCell>Documento</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>João Silva</Table.Cell>
                <Table.Cell>123.456.789-00</Table.Cell>
                <Table.Cell><span style={{ color: '#28a745', fontWeight: 600 }}>Ativo</span></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Maria Oliveira</Table.Cell>
                <Table.Cell>987.654.321-11</Table.Cell>
                <Table.Cell><span style={{ color: '#dc3545', fontWeight: 600 }}>Inativo</span></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Tabela Zebrada (Striped)</Typography>
          <Table striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Produto</Table.HeaderCell>
                <Table.HeaderCell>Categoria</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Notebook Gamer</Table.Cell>
                <Table.Cell>Eletrônicos</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Mouse Sem Fio</Table.Cell>
                <Table.Cell>Acessórios</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderCards = () => (
    <React.Fragment>
      <ExampleSection title="Cartões (Cards)">
        {/* Row 1: Info Cards */}
        <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Cards de Informação</Typography>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
          <Card>
            <Card.Header title="Nome da Empresa">
              <Dropdown>
                <Dropdown.Toggle />
                <Dropdown.Menu>
                  <Dropdown.Item icon="fas fa-edit">Editar</Dropdown.Item>
                  <Dropdown.Item icon="fas fa-trash-alt" style={{ color: colors.error }}>Excluir</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Card.Header>
            <Card.Body>
              <Typography>CNPJ: 00.000.000/0001-00</Typography>
              <Typography>Plano: Professional Gold</Typography>
              <Typography style={{ color: colors.success }}>Assinatura Ativa</Typography>
            </Card.Body>
            <Card.Footer>
              <Button variant="primary">Selecionar Empresa</Button>
            </Card.Footer>
          </Card>
        </div>

        {/* Row 2: Stat Cards */}
        <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Cards de KPI / Indicadores</Typography>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
          <Card style={{ borderLeft: `4px solid ${colors.success}` }}>
            <Card.Body>
              <Typography size="xs" style={{ color: colors.brandMediumGray, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Receitas</Typography>
              <Typography size="xl" style={{ fontWeight: 800, color: colors.success, margin: '8px 0' }}>R$ 45.230,00</Typography>
            </Card.Body>
          </Card>
          <Card style={{ borderLeft: `4px solid ${colors.error}` }}>
            <Card.Body>
              <Typography size="xs" style={{ color: colors.brandMediumGray, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Despesas</Typography>
              <Typography size="xl" style={{ fontWeight: 800, color: colors.error, margin: '8px 0' }}>R$ 18.750,00</Typography>
            </Card.Body>
          </Card>
        </div>

        {/* Row 3: Simple Card */}
        <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Card Simples (só Body)</Typography>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
          <Card>
            <Card.Body>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: 40, height: 40, borderRadius: '10px', background: `linear-gradient(135deg, ${colors.primary}, ${colors.brandLightBlue})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-bolt" style={{ color: '#fff' }}></i>
                </div>
                <Typography size="md" style={{ fontWeight: 700 }}>Performance</Typography>
              </div>
              <Typography size="xs" style={{ color: colors.brandMediumGray, lineHeight: 1.6 }}>
                Monitore a performance do seu sistema em tempo real com dashboards.
              </Typography>
            </Card.Body>
          </Card>
        </div>

        {/* Row 4: Compound Header */}
        <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Card com Header Composto (ícone + título)</Typography>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '2rem' }}>
          <Card>
            <Card.Header title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-file-invoice-dollar" style={{ color: colors.primary }}></i>
                Resumo Financeiro
              </div>
            } />
            <Card.Body>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.brandOffWhite}` }}>
                <Typography size="sm" style={{ color: colors.brandMediumGray }}>Total Faturado</Typography>
                <Typography size="sm" style={{ fontWeight: 700 }}>R$ 125.000,00</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.brandOffWhite}` }}>
                <Typography size="sm" style={{ color: colors.brandMediumGray }}>Total Pago</Typography>
                <Typography size="sm" style={{ fontWeight: 700, color: colors.success }}>R$ 98.500,00</Typography>
              </div>
            </Card.Body>
            <Card.Footer>
              <Button variant="secondary" size="sm">Ver Relatório Completo</Button>
            </Card.Footer>
          </Card>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderCharts = () => (
    <React.Fragment>
      <ExampleSection title="Gráficos (Charts)">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '3rem' }}>
          <Card>
            <Card.Header title="Vendas Mensais (Barra)" />
            <Card.Body>
              <Chart 
                type="bar" 
                data={{
                  labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
                  datasets: [{
                    label: 'Faturamento (R$)',
                    data: [12000, 19000, 15000, 25000, 22000, 30000],
                    backgroundColor: colors.brandLightBlue,
                    borderRadius: 4,
                  }]
                }} 
              />
            </Card.Body>
          </Card>

          <Card>
            <Card.Header title="Crescimento de Clientes (Linha)" />
            <Card.Body>
              <Chart 
                type="line" 
                data={{
                  labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
                  datasets: [{
                    label: 'Novos Clientes',
                    data: [50, 80, 65, 120, 110, 160],
                    borderColor: colors.brandYellow,
                    backgroundColor: 'rgba(242, 192, 26, 0.1)',
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#ffffff',
                    pointBorderColor: colors.brandYellow,
                    pointBorderWidth: 2,
                    pointRadius: 5,
                  }]
                }} 
              />
            </Card.Body>
          </Card>
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderAlertas = () => (
    <React.Fragment>
      <ExampleSection title="Alertas de Sistema (SweetAlert)">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          Modais de feedback e confirmação global (Sucesso, Warning, Erro).
        </Typography>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Button variant="success" onClick={() => setActiveAlert('success')}>Ver Alerta de Sucesso</Button>
          <Button variant="warning" onClick={() => setActiveAlert('warning')}>Ver Alerta de Atenção</Button>
          <Button variant="danger" onClick={() => setActiveAlert('error')}>Ver Alerta de Erro</Button>
        </div>

        {activeAlert === 'success' && (
          <SweetAlert 
            variant="success" 
            title="Operação Realizada!" 
            onConfirm={closeAlert}
          >
            Seus dados foram salvos com sucesso no sistema.
          </SweetAlert>
        )}

        {activeAlert === 'warning' && (
          <SweetAlert 
            variant="warning" 
            title="Tem Certeza?" 
            onConfirm={closeAlert}
            onCancel={closeAlert}
            showCancel
            confirmBtnText="Sim, prosseguir"
          >
            Esta ação irá modificar múltiplos registros. Deseja realmente prosseguir?
          </SweetAlert>
        )}

        {activeAlert === 'error' && (
          <SweetAlert 
            variant="error" 
            title="Ops! Algo deu errado." 
            onConfirm={closeAlert}
          >
            Não foi possível salvar os registros devido a uma falha de comunicação com o servidor.
          </SweetAlert>
        )}
      </ExampleSection>
    </React.Fragment>
  );

  const renderArquivosTooltips = () => (
    <React.Fragment>
      <ExampleSection title="Input de arquivo WEB">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          Um file input possibilita ao usuário enviar um ou vários arquivos de seu computador. Pode se apresentar em 6 estados:
          repouso, dropping, carregamento, preenchido, erro e desabilitado.
        </Typography>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem', maxWidth: '600px' }}>
          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>REPOUSO</Typography>
            <FileInput 
              label="Envie um arquivo" 
              helperText="Abaixo do campo liste as restrições do arquivo a ser enviado, se houver:
- Extensões aceitas: pdf, png ou jpeg
- Tamanho máximo permitido: 10mb
- Máximo de arquivos: 1" 
            />
          </div>

          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>DROPPING (Hover / Drag)</Typography>
            {/* We simulate dropping by styling it manually via a wrapper or simply letting the user test it live, but we can force state with a prop if we added one (not strictly necessary for the example unless built in) -> user can drag a file. For demonstration we pass a fake style representation */}
            <div style={{ pointerEvents: 'none' }}>
              <FileInput label="Envie um arquivo" />
              <Typography size="xsText" style={{ marginTop: '0.5rem', fontStyle: 'italic', color: colors.brandMediumGray }}>
                (Para ver o estado dropping, arraste um arquivo sobre a área acima)
              </Typography>
            </div>
          </div>

          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>CARREGAMENTO</Typography>
            <FileInput 
              label="Envie um arquivo" 
              files={[{ name: "FileName.pdf" }]}
              loading={45} 
            />
          </div>

          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>PREENCHIDO</Typography>
            <FileInput 
              label="Envie um arquivo" 
              files={[{ name: "FileName.pdf" }, { name: "FileName-Exemplo.jpg" }]}
            />
          </div>

          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>ERRO</Typography>
            <FileInput 
              label="Envie um arquivo" 
              files={[{ name: "FileName-Exemplo.csv" }]}
              error="Formato de arquivo não suportado"
            />
          </div>

          <div>
            <Typography size="sm" style={{ color: colors.brandLightGray, fontWeight: 'bold', display: 'block', marginBottom: '1rem' }}>DESABILITADO</Typography>
            <FileInput label="Envie um arquivo" disabled />
          </div>
        </div>
      </ExampleSection>

      <ExampleSection title="Tooltip WEB">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          Tooltips são acionadas no hover e dispensadas automaticamente quando o usuário deixa de interagir com a área. 
          São flexíveis em tamanho e podem aparecer sobre o objeto de interação.
        </Typography>

        <div style={{ display: 'flex', gap: '1rem', backgroundColor: '#fff', padding: '1rem', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}`, width: 'max-content' }}>
          <Tooltip content="Negrito">
            <Button variant="light" size="sm" shape="rounded" style={{ padding: '4px 12px', minWidth: 'auto', fontWeight: 'bold' }}>B</Button>
          </Tooltip>
          <Tooltip content="Itálico">
            <Button variant="light" size="sm" shape="rounded" style={{ padding: '4px 12px', minWidth: 'auto', fontStyle: 'italic' }}>I</Button>
          </Tooltip>
          <Tooltip content="Sublinhar">
            <Button variant="light" size="sm" shape="rounded" style={{ padding: '4px 12px', minWidth: 'auto', textDecoration: 'underline' }}>U</Button>
          </Tooltip>
          <Tooltip content="Apagar Formatação">
            <Button variant="light" size="sm" shape="rounded" style={{ padding: '4px 12px', minWidth: 'auto' }}>
              <i className="fas fa-eraser"></i>
            </Button>
          </Tooltip>
        </div>
      </ExampleSection>
    </React.Fragment>
  );
  
  const renderPassos = () => (
    <React.Fragment>
      <ExampleSection title="Steps Progress - Default">
        <div style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
          <Steps 
            current={1}
            items={[
              { title: 'Carrinho', description: 'Produtos selecionados' },
              { title: 'Pagamento', description: 'Forma de pagamento' },
              { title: 'Confirmação', description: 'Resumo do pedido' }
            ]}
          />
        </div>
      </ExampleSection>

      <ExampleSection title="Steps Progress - Completed">
        <div style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
          <Steps 
            current={3}
            items={[
              { title: 'Login' },
              { title: 'Endereço' },
              { title: 'Pagamento' },
              { title: 'Finalizado' }
            ]}
          />
        </div>
      </ExampleSection>

      <ExampleSection title="Steps Progress - Pending Start">
        <div style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}` }}>
          <Steps 
            current={0}
            items={[
              { title: 'Dados Básicos' },
              { title: 'Documentos' },
              { title: 'Assinatura' }
            ]}
          />
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const renderAddress = () => (
    <React.Fragment>
      <ExampleSection title="Componente Address Refatorado">
        <Typography variant="paragraph" as="p" style={{ display: 'block', marginBottom: '2rem', fontStyle: 'italic', fontSize: '14px' }}>
          O componente Address agora utiliza as entradas padrão do Design System e pode funcionar de forma reativa com as propriedades `data` e `onChange`.
        </Typography>

        <div style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', border: `1px solid ${colors.brandOffWhite}`, marginBottom: '2rem' }}>
          <Address 
            data={addressData}
            onChange={(newData) => setAddressData(newData)}
          />
        </div>

        <Typography size="md" style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold' }}>Estado do Endereço (Saída)</Typography>
        <div style={{ backgroundColor: '#1e1e1e', color: '#00ff00', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(addressData, null, 2)}
        </div>
      </ExampleSection>
    </React.Fragment>
  );

  const contentMap = {
    cores_tipografia: renderCoresTipografia,
    botoes: renderBotoes,
    inputs: renderInputs,
    tabelas: renderTabelas,
    cards: renderCards,
    charts: renderCharts,
    alertas: renderAlertas,
    arquivos_tooltips: renderArquivosTooltips,
    passos: renderPassos,
    address: renderAddress,
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: colors.brandOffWhite }}>
      {/* Sidebar navigation */}
      <div style={{ 
        width: '280px', 
        backgroundColor: '#fff', 
        borderRight: `1px solid ${colors.brandOffWhite}`,
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto'
      }}>
        <Typography size="lg" style={{ fontWeight: 'bold', marginBottom: '2rem', paddingLeft: '1rem', color: colors.brandDarkBlue }}>
          DS UI Kit Base
        </Typography>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem 1rem',
                backgroundColor: activeTab === item.id ? colors.brandIce : 'transparent',
                color: activeTab === item.id ? colors.brandDarkBlue : colors.brandMediumGray,
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                fontWeight: activeTab === item.id ? '600' : '400',
                transition: 'all 0.2s',
              }}
            >
              <i className={`fas ${item.icon}`} style={{ width: '20px', textAlign: 'center', color: activeTab === item.id ? colors.brandLightBlue : colors.brandMediumGray }}></i>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, padding: '3rem 4rem', overflowY: 'auto', backgroundColor: '#fafbfc' }}>
        <Typography size="xl" style={{ marginBottom: '0.5rem', display: 'block', fontWeight: 'bold' }}>
          {(menuItems.find(item => item.id === activeTab) || {}).label}
        </Typography>
        <div style={{ width: '100%', height: '1px', backgroundColor: colors.brandLightGray, opacity: 0.3, marginBottom: '2rem' }}></div>
        
        {contentMap[activeTab] && contentMap[activeTab]()}
      </div>
    </div>
  );
};

export default DesignSystemExamples;



// WEBPACK FOOTER //
// ./src/views/AppViews/DesignSystemExamples.jsx